<template>
    <div class="main">

        <nav aria-label="breadcrumb">
            <ol class="breadcrumb">
                <li class="breadcrumb-item"><a :href="'/eecdView/' + geneLocus">{{geneLocus}}</a></li>
                <li class="breadcrumb-item"><a :href="'/assayList/' + geneLocus + '/' + dsRnaName + '/' + rnaId ">{{dsRnaName}}</a></li>
                <li class="breadcrumb-item active" aria-current="page">{{dicName}}</li>
            </ol>
        </nav>

        <h2 class="round">DIC Result</h2>

        <div class="mainContent">
            <fetch-experiment :summary-id="experimentId">
                <template slot-scope="{response, loading}">
                    <div v-if="loading">Loading...</div>
                    <div v-else-if="Object.keys(response).length > 0">

                    <h4 class="round">Experiment Details</h4>
                        <div class="panel panel-default">
                            <div class="panel-body">
                                <div class="container-fluid">
                                    <div class="row">
                                        <div class="text-primary col-sm-2">dsRNA:</div>
                                        <div class="col-sm-10">{{dsRnaName}}</div>
                                    </div>
                                    <div class="row">
                                        <div class="text-primary col-sm-2">Experiment:</div>
                                        <div class="col-sm-10">{{dicName}}</div>
                                    </div>
                                    <div class="row">
                                        <div class="text-primary col-sm-2">Description:</div>
                                        <div class="col-sm-10" v-html="response.description"></div>
                                    </div>
                                    <div class="row">
                                        <div class="text-primary col-sm-2">Observed Phenotype:</div>
                                        <div class="col-sm-10">{{response.className}}</div>
                                    </div>
                                    <div class="row">
                                        <div class="text-primary col-sm-2">Comment:</div>
                                        <div class="col-sm-10">{{response.comment}}</div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </template>
            </fetch-experiment>

            <fetch-recordings :summary-id="experimentId">
                <template slot-scope="{response, loading}">
                    <div v-if="loading">Loading...</div>
                    <div v-else-if="Object.keys(response).length > 0">
                        <div class="row">
                            <div class="col-sm-8">
                                <h4 class="round">Recordings (Movies)</h4>
                                <div class="panel panel-default table-responsive">
                                    <table class="table table-striped table-hover">
                                        <thead>
                                        <tr>
                                            <th>Recordings</th>
                                            <th v-for="col in response[0].item" v-bind:key="col.category">{{col.category}}</th>
                                        </tr>
                                        </thead>
                                        <tbody>
                                            <tr v-for="(row, index) in orderBy(response.slice(1), 'recordingID')" v-bind:key="index">
                                                <th scope="row" v-bind:class="{ 'playing': currentIndex === index }">
                                                    {{row.recordingID}}
                                                    <template v-if="checkMovie(row.fileName, row.fileType)">
                                                        <button v-on:click="playMovie(index, row.fileName, row.fileType)">
                                                            <i class="glyphicon glyphicon-play" aria-hidden="true"></i>
                                                        </button>
                                                    </template>
                                                </th>
                                                <td v-for="col in response[0].item" v-bind:key="row.recordingID + col.category"
                                                    v-bind:class="[defineClass(col.category, row.item)]">
                                                    {{renderCell(col.category, row.item)}}
                                                </td>
                                            </tr>
                                        </tbody>
                                    </table>
                                </div>
                            </div>

                            <div class="col-sm-4">
                                <h4 class="round">Recording {{currentIndex + 1}}</h4>
                                <div class="panel panel-default">

                                        <div v-show="currentType === 'movie'">
                                            <video ref="video" controls loop autoplay>
                                                <source ref="source" :src="current.src" type="video/mp4">
                                                Sorry, your browser doesn't support embedded videos.
                                            </video>
                                            <a :href="current.src">
                                                <div class="blue">Link to Movie</div>
                                            </a>
                                        </div>
                                        <div v-show="currentType === 'image'">
                                            <div class="important">
                                                Only a still image is available for the selected recording. Please click the link below to view the fullsize image in a new window.
                                            </div>
                                            <img :src="current.src"/>
                                            <a :href="current.src">
                                                <div class="blue">Link to Large Image</div>
                                            </a>
                                        </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </template>
            </fetch-recordings>
        </div>
    </div>
</template>

<script>
  import FetchExperiment from './FetchExperiment.vue'
  import FetchRecordings from './FetchRecordings.vue'

  const _ = require('lodash');

  export default {
    name: "AssayList",
    components: {
      FetchExperiment, FetchRecordings,
    },
    data() {
      return {
        currentIndex: 0,
        current: {},
        currentType: '',
        geneLocus: this.$route.params.geneLocus,
        dsRnaName: this.$route.params.name,
        rnaId: this.$route.params.rnaId,
        experimentId: this.$route.params.experimentId,
        dicName: this.$route.params.dicName,
        publicPath: process.env.BASE_URL
      }
    },
    methods: {
      orderBy(col, key) {
        return _.orderBy(col, key)
      },
      renderCell(category, item) {
        let found = _.find(item, { category })
        if(found) {
          return found.comment === ''? 'No comment available' : found.comment
        }
        return '-'
      },
      defineClass(category, item) {
        let found = _.find(item, { category })
        if(found) {
          return [found.severity]
        }
        return ['normal'];
      },
      playMovie(idx, name, type) {
        const vm = this
        vm.currentIndex = idx
        vm.current.name = name
        vm.current.type = type
        vm.currentType = type

        if(type === 'movie') {
          vm.$refs.video.firstChild.src = vm.getContent(encodeURIComponent(name) + '.mp4')
          vm.$refs.video.load()
          vm.current.src = vm.getContent(encodeURIComponent(name) + '.mp4')
        } else if(type === 'image') {
          vm.current.src = vm.getContent(encodeURIComponent(name) + '.jpg')
        }
      },
      checkMovie(name, type) {
        const vm = this
        if(name && !_.isEmpty(name) && type && !_.isEmpty(name)) {
          vm.current.name = name
          vm.current.type = type
          if(vm.current.src === undefined) {
            vm.currentType = type
            if(type === 'movie') {
              vm.current.src = vm.getContent(encodeURIComponent(name) + '.mp4')
            } else if(type === 'image') {
              vm.current.src = vm.getContent(encodeURIComponent(name) + '.jpg')
            }
          }
          return true;
        }
        else return false;
      },
      getParentFolder(url) {
        let tokens = url.split('-')
        return tokens[0];
      },
      getContent(url) {
        const vm = this
        let parent = vm.getParentFolder(url)
        return `/ceimages/${parent}/${url}`
      }
    },
    mounted() {
      // const vm = this
      // vm.images = []
      // /* eslint-disable no-console */
      // console.log(vm.dsRnaName);
    },
  }
</script>

<style>
    @import url('~@/assets/bootstrap.css');
    @import url('~@/assets/datatable.css');

    .important {
        border: 1px solid #aaaaaa;
        padding: 0.3em;
        margin-bottom: 1em;
        background-color: #EEB8B4;
    }

    .main {
        margin: 1.5rem;
    }

    .clear {
        background-color: #c7eedc;
    }

    .ambiguous {
        background-color: #b1aeb4;
    }

    .severe {
        background-color: #ee9c97;
    }

    .playing {
        background-color: lightgreen;
    }

    .container {
        position: relative;
        text-align: center;
        color: white;
    }

    .body {
        font-weight: bold;
        font-size: 12px;
    }

    .breadcrumb {
        font-size: 1.3rem;
    }

    .breadcrumb a {
        display: inline;
        padding: 0 0.5em;
    }

    .row {
        font-size: 1.2rem;
    }

    .centered {
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
        font-weight: bold;
        font-size: 20px;
        text-decoration: underline;
    }

    .blue {
        color: #2834ff;
    }

    .white {
        color: white;
    }

    .round {
        font-family: Calibri, Candara, Segoe, Segoe UI, Optima, Arial;
        border-radius: 7px;
        border: 1px solid #d9d9d9;
        background: #dae3f3;
        padding: 3px 7px 3px 7px;
        font-style: oblique;
        color: #203864;
        font-weight: bold;
    }

    p.strong {
        text-decoration: underline;
        font-style: oblique;
    }

    h2.card-title {
        margin-top: 0;
        color: #2f5597;
        font-style: oblique;
    }

    h5.card-title {
        color: #2f5597;
        font-style: oblique;
        font-weight: bold;
    }

    .mainContent {
        border-radius: 25px;
        border: 2px solid #dae3f3;
        padding: 20px;
    }
</style>